import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchTopUpPacks } from '@/lib/api';
import { METER_LABEL } from '@/lib/billing';
import { formatPrice, type CurrencyCode } from '@/lib/pricing';
import './TopUpSheet.css';

/**
 * Top-up packs.
 *
 * A real sheet listing purchasable packs, each a clickable row that opens checkout — not a
 * placeholder. Ask Aware packs only appear where the plan includes Ask Aware at all.
 *
 * These are the balances that carry over on a plan change: bought outright, never expiring,
 * and tracked separately from the monthly allowance. The copy says so, because that promise is
 * what makes the carry-over rule legible when a customer later changes plan.
 */
export function TopUpSheet({
  currency,
  hasAskAware,
  onClose,
}: {
  currency: CurrencyCode;
  hasAskAware: boolean;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['top-up-packs', currency],
    queryFn: () => fetchTopUpPacks(currency),
  });

  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const packs = (data ?? []).filter((pack) => hasAskAware || pack.meter !== 'askAware');

  return (
    <div className="topUp" role="dialog" aria-modal="true" aria-label="Add a top-up pack">
      <header className="topUp__head">
        <div>
          <p className="u-meta">One-time purchase</p>
          <h2 className="topUp__title">Add a top-up pack</h2>
        </div>
        <button ref={closeRef} type="button" className="topUp__close" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="topUp__body">
        {isLoading && <p className="topUp__empty">Loading…</p>}
        <ul className="topUp__list">
          {packs.map((pack) => {
            const meter = METER_LABEL[pack.meter];
            return (
              <li key={pack.id}>
                <button type="button" className="topUp__pack">
                  <span className="topUp__packMain">
                    <span className="topUp__packQty">
                      {pack.quantity.toLocaleString('en-US')} {meter.unit}
                    </span>
                    <span className="topUp__packMeter">{meter.name}</span>
                  </span>
                  <span className="topUp__packPrice">{formatPrice(currency, pack.price)}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <p className="topUp__note">
          Packs never expire and stack on top of your monthly allowance. If you change plan
          later, whatever is left carries over.
        </p>
      </div>
    </div>
  );
}
